import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getOrganization, updateOrganizationPolicy, getCurrentSubscription, getSubscriptionPlans, createCheckoutSession, getCheckoutStatus, getPaymentTransactions, getEmailSettings, updateEmailSettings, getSupportedCurrencies } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Settings, Building2, Save, CreditCard, Check, Star, Zap, Crown, Mail, Loader2, Globe } from 'lucide-react';

export const SettingsPage = () => {
  const [searchParams] = useSearchParams();
  const [organization, setOrganization] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('usd');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [upgrading, setUpgrading] = useState(null);
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    email_enabled: true,
    custom_from_name: '',
    notify_schedule_changes: true,
    notify_vacation_updates: true,
    notify_swap_requests: true,
    notify_flagged_entries: true
  });
  const [savingEmail, setSavingEmail] = useState(false);

  const [policyData, setPolicyData] = useState({
    max_departments: 10,
    max_branches: 20
  });

  const fetchData = useCallback(async () => {
    try {
      const [orgRes, subRes, plansRes, txRes, emailRes, currRes] = await Promise.all([
        getOrganization(),
        getCurrentSubscription(),
        getSubscriptionPlans(),
        getPaymentTransactions().catch(() => ({ data: [] })),
        getEmailSettings().catch(() => ({ data: null })),
        getSupportedCurrencies().catch(() => ({ data: { currencies: [] } }))
      ]);
      setOrganization(orgRes.data);
      setSubscription(subRes.data);
      setPlans(plansRes.data);
      setTransactions(txRes.data);
      if (currRes.data?.currencies) {
        setCurrencies(currRes.data.currencies);
      }
      if (emailRes.data && !emailRes.data.message) {
        setEmailSettings({
          email_enabled: emailRes.data.email_enabled ?? true,
          custom_from_name: emailRes.data.custom_from_name || '',
          notify_schedule_changes: emailRes.data.notify_schedule_changes ?? true,
          notify_vacation_updates: emailRes.data.notify_vacation_updates ?? true,
          notify_swap_requests: emailRes.data.notify_swap_requests ?? true,
          notify_flagged_entries: emailRes.data.notify_flagged_entries ?? true
        });
      }
      setPolicyData({
        max_departments: orgRes.data.max_departments || 10,
        max_branches: orgRes.data.max_branches || 20
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await getCheckoutStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription has been upgraded.');
        fetchData();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  }, [fetchData]);

  // Handle payment redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled');
    }
  }, [pollPaymentStatus, searchParams]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateOrganizationPolicy(policyData);
      toast.success('Settings saved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpgrade = async (tier) => {
    if (tier === 'free') {
      toast.info('You are already on the free tier');
      return;
    }
    
    setUpgrading(tier);
    try {
      const originUrl = window.location.origin;
      const response = await createCheckoutSession(tier, originUrl, selectedCurrency);
      // Redirect to payment checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setUpgrading(null);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      await updateEmailSettings(emailSettings);
      toast.success('Email notification settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSavingEmail(false);
    }
  };

  const getSelectedCurrencyInfo = () => {
    return currencies.find(c => c.code === selectedCurrency) || { symbol: '$', name: 'US Dollar' };
  };

  const getPlanPrice = (tier) => {
    const currencyInfo = currencies.find(c => c.code === selectedCurrency);
    if (currencyInfo?.pricing?.[tier]) {
      return currencyInfo.pricing[tier];
    }
    // Fallback
    return tier === 'pro' ? '$29.99' : tier === 'enterprise' ? '$99.99' : 'Free';
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'pro': return <Star className="w-6 h-6 text-blue-500" />;
      case 'enterprise': return <Crown className="w-6 h-6 text-violet-500" />;
      default: return <Zap className="w-6 h-6 text-slate-500" />;
    }
  };

  const isCurrentPlan = (tier) => subscription?.tier === tier;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your organization settings, policies, and subscription
        </p>
      </div>

      {/* Current Subscription */}
      <Card data-testid="current-subscription-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <CreditCard className="w-5 h-5" />
            Current Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-50 to-violet-50 flex items-center justify-center">
              {getPlanIcon(subscription?.tier)}
            </div>
            <div>
              <h3 className="text-xl font-bold capitalize">{subscription?.tier} Plan</h3>
              <p className="text-muted-foreground">
                {subscription?.max_employees === -1 ? 'Unlimited' : subscription?.max_employees} employees, 
                {' '}{subscription?.max_departments === -1 ? 'Unlimited' : subscription?.max_departments} departments,
                {' '}{subscription?.max_branches === -1 ? 'Unlimited' : subscription?.max_branches} branches
              </p>
            </div>
            <Badge className={subscription?.status === 'active' ? 'badge-success ml-auto' : 'badge-warning ml-auto'}>
              {subscription?.status}
            </Badge>
          </div>
          
          {/* Features */}
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm font-medium mb-2">Features included:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(subscription?.features || {}).map(([feature, enabled]) => (
                <Badge 
                  key={feature} 
                  variant="outline" 
                  className={enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400 line-through'}
                >
                  {feature.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upgrade Plans */}
      <Card data-testid="subscription-plans-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="font-['Manrope']">Upgrade Your Plan</CardTitle>
              <CardDescription>Choose the plan that fits your organization</CardDescription>
            </div>
            {currencies.length > 1 && (
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                  <SelectTrigger className="w-[200px]" data-testid="currency-select">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((curr) => (
                      <SelectItem key={curr.code} value={curr.code}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{curr.symbol}</span>
                          <span>{curr.name}</span>
                          {curr.region === 'Caribbean' && (
                            <Badge variant="outline" className="ml-1 text-xs">Caribbean</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <div
                key={plan.tier}
                className={`p-6 rounded-xl border-2 transition-all ${
                  isCurrentPlan(plan.tier)
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid={`plan-${plan.tier}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  {getPlanIcon(plan.tier)}
                  <div>
                    <h3 className="font-bold capitalize">{plan.tier}</h3>
                    <p className="text-2xl font-bold">
                      {plan.tier === 'free' ? 'Free' : getPlanPrice(plan.tier)}
                      {plan.tier !== 'free' && (
                        <span className="text-sm font-normal text-muted-foreground">/month</span>
                      )}
                    </p>
                  </div>
                </div>
                
                <ul className="space-y-2 mb-6 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    {plan.max_employees === -1 ? 'Unlimited' : plan.max_employees} employees
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    {plan.max_departments === -1 ? 'Unlimited' : plan.max_departments} departments
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    {plan.max_branches === -1 ? 'Unlimited' : plan.max_branches} branches
                  </li>
                </ul>

                {isCurrentPlan(plan.tier) ? (
                  <Button disabled className="w-full">
                    Current Plan
                  </Button>
                ) : plan.tier === 'free' ? (
                  <Button variant="outline" disabled className="w-full">
                    Free Tier
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleUpgrade(plan.tier)}
                    disabled={upgrading === plan.tier}
                    className="w-full"
                    data-testid={`upgrade-${plan.tier}-btn`}
                  >
                    {upgrading === plan.tier ? 'Processing...' : `Upgrade to ${plan.tier}`}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Organization Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <Building2 className="w-5 h-5" />
            Organization
          </CardTitle>
          <CardDescription>
            Basic information about your organization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-muted/50">
              <p className="text-sm text-muted-foreground">Organization Name</p>
              <p className="text-lg font-semibold mt-1">{organization?.name}</p>
            </div>
            <div className="p-4 rounded-xl bg-muted/50">
              <p className="text-sm text-muted-foreground">Organization ID</p>
              <p className="text-sm font-mono mt-1 break-all">{organization?.id}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Policy Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <Settings className="w-5 h-5" />
            Policy Settings
          </CardTitle>
          <CardDescription>
            Configure limits for your organization (within your subscription tier)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Maximum Departments</Label>
                <Input
                  type="number"
                  min="1"
                  max={subscription?.max_departments === -1 ? 1000 : subscription?.max_departments}
                  value={policyData.max_departments}
                  onChange={(e) => setPolicyData(prev => ({ 
                    ...prev, 
                    max_departments: parseInt(e.target.value) || 1 
                  }))}
                  data-testid="max-departments-input"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum: {subscription?.max_departments === -1 ? 'Unlimited' : subscription?.max_departments}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Maximum Branches</Label>
                <Input
                  type="number"
                  min="1"
                  max={subscription?.max_branches === -1 ? 1000 : subscription?.max_branches}
                  value={policyData.max_branches}
                  onChange={(e) => setPolicyData(prev => ({ 
                    ...prev, 
                    max_branches: parseInt(e.target.value) || 1 
                  }))}
                  data-testid="max-branches-input"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum: {subscription?.max_branches === -1 ? 'Unlimited' : subscription?.max_branches}
                </p>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={saving}
              className="rounded-full"
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Payment History */}
      {transactions.length > 0 && (
        <Card data-testid="payment-history-card">
          <CardHeader>
            <CardTitle className="font-['Manrope']">Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium capitalize">{tx.tier} Plan</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">${tx.amount.toFixed(2)}</p>
                    <Badge className={tx.payment_status === 'paid' ? 'badge-success' : 'badge-warning'}>
                      {tx.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <Mail className="w-5 h-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Configure which email notifications your organization receives
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email-enabled" className="font-medium">Enable Email Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive email notifications for important events</p>
            </div>
            <Switch
              id="email-enabled"
              checked={emailSettings.email_enabled}
              onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, email_enabled: checked }))}
              data-testid="email-enabled-switch"
            />
          </div>

          {emailSettings.email_enabled && (
            <>
              <div className="space-y-2">
                <Label htmlFor="custom-from-name">Custom From Name (Optional)</Label>
                <Input
                  id="custom-from-name"
                  placeholder="Leave empty to use default (TeamTime)"
                  value={emailSettings.custom_from_name}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, custom_from_name: e.target.value }))}
                  data-testid="custom-from-name"
                />
              </div>

              <div className="space-y-4">
                <Label className="font-medium">Notification Types</Label>
                
                <div className="flex items-center justify-between py-2 border-b">
                  <div>
                    <p className="font-medium text-sm">Schedule Changes</p>
                    <p className="text-xs text-muted-foreground">New shifts and schedule updates</p>
                  </div>
                  <Switch
                    checked={emailSettings.notify_schedule_changes}
                    onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, notify_schedule_changes: checked }))}
                    data-testid="notify-schedule-switch"
                  />
                </div>

                <div className="flex items-center justify-between py-2 border-b">
                  <div>
                    <p className="font-medium text-sm">Vacation Updates</p>
                    <p className="text-xs text-muted-foreground">Vacation request approvals and rejections</p>
                  </div>
                  <Switch
                    checked={emailSettings.notify_vacation_updates}
                    onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, notify_vacation_updates: checked }))}
                    data-testid="notify-vacation-switch"
                  />
                </div>

                <div className="flex items-center justify-between py-2 border-b">
                  <div>
                    <p className="font-medium text-sm">Shift Swap Requests</p>
                    <p className="text-xs text-muted-foreground">When employees request shift swaps</p>
                  </div>
                  <Switch
                    checked={emailSettings.notify_swap_requests}
                    onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, notify_swap_requests: checked }))}
                    data-testid="notify-swap-switch"
                  />
                </div>

                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-sm">Flagged Entries</p>
                    <p className="text-xs text-muted-foreground">Clock-ins outside geo-fence area</p>
                  </div>
                  <Switch
                    checked={emailSettings.notify_flagged_entries}
                    onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, notify_flagged_entries: checked }))}
                    data-testid="notify-flagged-switch"
                  />
                </div>
              </div>
            </>
          )}

          <Button 
            onClick={handleSaveEmailSettings} 
            disabled={savingEmail}
            data-testid="save-email-settings-btn"
          >
            {savingEmail ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Email Settings
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Share Organization ID */}
      <Card>
        <CardHeader>
          <CardTitle className="font-['Manrope']">Invite Employees</CardTitle>
          <CardDescription>
            Share your organization ID with employees so they can join
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 rounded-xl bg-muted font-mono text-sm break-all">
            {organization?.id}
          </div>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              navigator.clipboard.writeText(organization?.id);
              toast.success('Organization ID copied to clipboard');
            }}
            data-testid="copy-org-id-btn"
          >
            Copy Organization ID
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
